import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, X, RefreshCw, Feather, UserPlus, Edit2, Save, Trash2,
  Star, Check, AlertCircle, Loader, ChevronLeft, ExternalLink
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

// ─── helpers ─────────────────────────────────────────────────────────────────

const avatarUrl = (raw) => {
  if (!raw) return null;
  if (raw.includes('cloudinary') && raw.includes('/upload/'))
    return raw.replace('/upload/', '/upload/c_thumb,g_face,r_max,w_80,h_80/');
  return raw;
};

const displayName = (u) =>
  u?.nickname || `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim() || u?.handle || '—';

// ─── Main component ───────────────────────────────────────────────────────────

const WritersAdmin = () => {
  // ── view state ──────────────────────────────────────────────────────────────
  const [tab, setTab] = useState('writers'); // 'writers' | 'add'

  // ── writers list ────────────────────────────────────────────────────────────
  const [writers, setWriters] = useState([]);
  const [loadingWriters, setLoadingWriters] = useState(true);
  const [writersError, setWritersError] = useState(null);
  const [writerSearch, setWriterSearch] = useState('');

  // ── edit inline ─────────────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState(null); // writer_profiles.id
  const [editForm, setEditForm] = useState({ tagline: '', is_featured: false });
  const [savingEdit, setSavingEdit] = useState(false);

  // ── add writer ──────────────────────────────────────────────────────────────
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addForm, setAddForm] = useState({ tagline: '' });
  const [addingWriter, setAddingWriter] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(null);

  // ── revoke confirm ──────────────────────────────────────────────────────────
  const [confirmRevoke, setConfirmRevoke] = useState(null); // writer object

  // ── load writers ─────────────────────────────────────────────────────────────

  const loadWriters = useCallback(async () => {
    setLoadingWriters(true);
    setWritersError(null);
    try {
      const { data, error } = await supabase
        .from('writer_profiles')
        .select('id, user_id, tagline, is_featured, cover_image, users(uuid, nickname, first_name, last_name, handle, profile_img, email)')
        .order('is_featured', { ascending: false });

      if (error) throw error;

      // Also fetch story counts per writer
      const uuids = (data || []).map((w) => w.user_id).filter(Boolean);
      let counts = {};
      if (uuids.length) {
        const { data: storyCounts } = await supabase
          .from('artspeaks_stories')
          .select('artist_uuid')
          .in('artist_uuid', uuids);
        (storyCounts || []).forEach((s) => {
          counts[s.artist_uuid] = (counts[s.artist_uuid] || 0) + 1;
        });
      }

      setWriters((data || []).map((w) => ({ ...w, _storyCount: counts[w.user_id] || 0 })));
    } catch (e) {
      setWritersError(e.message);
    } finally {
      setLoadingWriters(false);
    }
  }, []);

  useEffect(() => {
    loadWriters();
  }, [loadWriters]);

  // ── search all users ─────────────────────────────────────────────────────────

  const searchUsers = useCallback(async (q) => {
    if (!q.trim()) { setUserResults([]); return; }
    setSearchingUsers(true);
    try {
      const { data } = await supabase
        .from('users')
        .select('uuid, nickname, first_name, last_name, handle, email, profile_img, is_writer')
        .or(`nickname.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%,handle.ilike.%${q}%,email.ilike.%${q}%`)
        .limit(15);
      setUserResults(data || []);
    } catch (e) {
      setUserResults([]);
    } finally {
      setSearchingUsers(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchUsers(userSearch), 300);
    return () => clearTimeout(t);
  }, [userSearch, searchUsers]);

  // ── add writer action ────────────────────────────────────────────────────────

  const handleAddWriter = async () => {
    if (!selectedUser) return;
    setAddingWriter(true);
    setAddError(null);
    setAddSuccess(null);
    try {
      // 1. Set is_writer = true on the user
      const { error: userErr } = await supabase
        .from('users')
        .update({ is_writer: true })
        .eq('uuid', selectedUser.uuid);
      if (userErr) throw userErr;

      // 2. Upsert writer_profiles row
      const { error: profileErr } = await supabase
        .from('writer_profiles')
        .upsert(
          { user_id: selectedUser.uuid, tagline: addForm.tagline.trim(), is_featured: false },
          { onConflict: 'user_id' }
        );
      if (profileErr) throw profileErr;

      setAddSuccess(`${displayName(selectedUser)} has been added as a writer.`);
      setSelectedUser(null);
      setAddForm({ tagline: '' });
      setUserSearch('');
      setUserResults([]);
      loadWriters();
    } catch (e) {
      setAddError(e.message);
    } finally {
      setAddingWriter(false);
    }
  };

  // ── edit / save writer profile ────────────────────────────────────────────────

  const startEdit = (w) => {
    setEditingId(w.id);
    setEditForm({ tagline: w.tagline || '', is_featured: w.is_featured || false });
  };

  const saveEdit = async (w) => {
    setSavingEdit(true);
    try {
      const { error } = await supabase
        .from('writer_profiles')
        .update({ tagline: editForm.tagline.trim(), is_featured: editForm.is_featured })
        .eq('id', w.id);
      if (error) throw error;
      setEditingId(null);
      loadWriters();
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSavingEdit(false);
    }
  };

  // ── revoke writer ────────────────────────────────────────────────────────────

  const revokeWriter = async (w) => {
    try {
      await supabase.from('users').update({ is_writer: false }).eq('uuid', w.user_id);
      await supabase.from('writer_profiles').delete().eq('id', w.id);
      setConfirmRevoke(null);
      loadWriters();
    } catch (e) {
      alert('Error revoking: ' + e.message);
    }
  };

  // ── filtered writers ─────────────────────────────────────────────────────────

  const filtered = writers.filter((w) => {
    if (!writerSearch.trim()) return true;
    const q = writerSearch.toLowerCase();
    const u = w.users || {};
    return (
      (u.nickname || '').toLowerCase().includes(q) ||
      (u.first_name || '').toLowerCase().includes(q) ||
      (u.last_name || '').toLowerCase().includes(q) ||
      (u.handle || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (w.tagline || '').toLowerCase().includes(q)
    );
  });

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Feather size={22} style={{ color: '#6366f1' }} />
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Writers</h2>
          <span style={{ background: '#e0e7ff', color: '#4338ca', borderRadius: 12, padding: '2px 10px', fontSize: 13 }}>
            {writers.length}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={loadWriters}
            style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={() => { setTab('add'); setAddSuccess(null); setAddError(null); }}
            style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}
          >
            <UserPlus size={14} /> Add Writer
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
        {['writers', 'add'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 16px', fontSize: 14, fontWeight: tab === t ? 700 : 400,
              color: tab === t ? '#6366f1' : '#6b7280',
              borderBottom: tab === t ? '2px solid #6366f1' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {t === 'writers' ? `Writers (${writers.length})` : 'Add Writer'}
          </button>
        ))}
      </div>

      {/* ── Writers list tab ── */}
      {tab === 'writers' && (
        <div>
          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              value={writerSearch}
              onChange={(e) => setWriterSearch(e.target.value)}
              placeholder="Search writers…"
              style={{ width: '100%', padding: '8px 8px 8px 34px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
            />
            {writerSearch && (
              <X size={14} onClick={() => setWriterSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', cursor: 'pointer' }} />
            )}
          </div>

          {loadingWriters && (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
              <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <p>Loading writers…</p>
            </div>
          )}
          {writersError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 16, color: '#dc2626', display: 'flex', gap: 8 }}>
              <AlertCircle size={16} /> {writersError}
            </div>
          )}

          {!loadingWriters && !writersError && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
              {writerSearch ? 'No writers match that search.' : 'No writers yet. Use "Add Writer" to get started.'}
            </div>
          )}

          {!loadingWriters && filtered.map((w) => {
            const u = w.users || {};
            const isEditing = editingId === w.id;
            const img = avatarUrl(u.profile_img);
            return (
              <div key={w.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 12, background: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  {/* Avatar */}
                  <div style={{ width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', background: '#e5e7eb', flexShrink: 0 }}>
                    {img
                      ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 20 }}>✎</div>
                    }
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{displayName(u)}</span>
                      {u.handle && <span style={{ color: '#6b7280', fontSize: 13 }}>@{u.handle}</span>}
                      {w.is_featured && (
                        <span style={{ background: '#fef9c3', color: '#92400e', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Star size={10} /> Featured
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: 13, marginTop: 2 }}>{u.email}</div>

                    {isEditing ? (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <input
                          value={editForm.tagline}
                          onChange={(e) => setEditForm((f) => ({ ...f, tagline: e.target.value }))}
                          placeholder="Tagline…"
                          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}
                        />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={editForm.is_featured}
                            onChange={(e) => setEditForm((f) => ({ ...f, is_featured: e.target.checked }))}
                          />
                          Featured writer (shown at top of directory)
                        </label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => saveEdit(w)}
                            disabled={savingEdit}
                            style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}
                          >
                            {savingEdit ? <Loader size={13} /> : <Save size={13} />} Save
                          </button>
                          <button onClick={() => setEditingId(null)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#374151', fontSize: 13, marginTop: 4, fontStyle: w.tagline ? 'normal' : 'italic', color: w.tagline ? '#374151' : '#9ca3af' }}>
                        {w.tagline || 'No tagline set'}
                      </div>
                    )}
                  </div>

                  {/* Story count + actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                    <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: 10, padding: '2px 10px', fontSize: 12 }}>
                      {w._storyCount} {w._storyCount === 1 ? 'story' : 'stories'}
                    </span>
                    {!isEditing && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => startEdit(w)} title="Edit profile" style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                          <Edit2 size={13} style={{ color: '#6b7280' }} />
                        </button>
                        {u.handle && (
                          <a href={`/writer/${u.handle}`} target="_blank" rel="noreferrer" title="View public page" style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <ExternalLink size={13} style={{ color: '#6b7280' }} />
                          </a>
                        )}
                        <button onClick={() => setConfirmRevoke(w)} title="Revoke writer status" style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                          <Trash2 size={13} style={{ color: '#ef4444' }} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add Writer tab ── */}
      {tab === 'add' && (
        <div style={{ maxWidth: 560 }}>
          <button
            onClick={() => setTab('writers')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280', fontSize: 13, marginBottom: 20, padding: 0 }}
          >
            <ChevronLeft size={16} /> Back to writers
          </button>

          <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700 }}>Add a writer</h3>
          <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 13 }}>
            Search for an existing user. Adding them will set <code>is_writer = true</code> on their account and create a writer profile.
          </p>

          {addSuccess && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: 14, color: '#15803d', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Check size={16} /> {addSuccess}
            </div>
          )}
          {addError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 14, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <AlertCircle size={16} /> {addError}
            </div>
          )}

          {/* User search */}
          {!selectedUser && (
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Search users</label>
              <div style={{ position: 'relative' }}>
                <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Name, handle, or email…"
                  style={{ width: '100%', padding: '9px 9px 9px 34px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
                {searchingUsers && <Loader size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />}
              </div>

              {userResults.length > 0 && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 4, overflow: 'hidden' }}>
                  {userResults.map((u) => {
                    const img = avatarUrl(u.profile_img);
                    return (
                      <div
                        key={u.uuid}
                        onClick={() => { setSelectedUser(u); setUserSearch(''); setUserResults([]); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', background: '#fff' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', background: '#e5e7eb', flexShrink: 0 }}>
                          {img ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{displayName(u)}</div>
                          <div style={{ color: '#6b7280', fontSize: 12 }}>{u.handle ? `@${u.handle} · ` : ''}{u.email}</div>
                        </div>
                        {u.is_writer && (
                          <span style={{ background: '#ede9fe', color: '#7c3aed', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>Already a writer</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {userSearch.trim() && !searchingUsers && userResults.length === 0 && (
                <div style={{ color: '#9ca3af', fontSize: 13, padding: '8px 4px' }}>No users found.</div>
              )}
            </div>
          )}

          {/* Selected user + form */}
          {selectedUser && (
            <div>
              <div style={{ border: '1px solid #a5b4fc', borderRadius: 10, padding: 14, background: '#f5f3ff', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', background: '#c4b5fd', flexShrink: 0 }}>
                  {avatarUrl(selectedUser.profile_img) && <img src={avatarUrl(selectedUser.profile_img)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{displayName(selectedUser)}</div>
                  <div style={{ color: '#7c3aed', fontSize: 13 }}>{selectedUser.handle ? `@${selectedUser.handle}` : selectedUser.email}</div>
                </div>
                <button onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed' }}>
                  <X size={16} />
                </button>
              </div>

              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>
                Tagline <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
              </label>
              <input
                value={addForm.tagline}
                onChange={(e) => setAddForm({ tagline: e.target.value })}
                placeholder="e.g. Poet · Visual storyteller"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 20 }}
              />

              {selectedUser.is_writer && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 12, color: '#92400e', fontSize: 13, marginBottom: 16 }}>
                  This user already has writer status. Submitting will update their writer profile tagline.
                </div>
              )}

              <button
                onClick={handleAddWriter}
                disabled={addingWriter}
                style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: addingWriter ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, opacity: addingWriter ? 0.7 : 1 }}
              >
                {addingWriter ? <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <UserPlus size={15} />}
                {selectedUser.is_writer ? 'Update Writer Profile' : 'Add as Writer'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Revoke confirm modal ── */}
      {confirmRevoke && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 400, width: '90%' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 17 }}>Revoke writer status?</h3>
            <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14 }}>
              This will set <strong>{displayName(confirmRevoke.users)}</strong>'s <code>is_writer</code> to false and delete their writer profile. Their stories will remain in the database.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmRevoke(null)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
              <button onClick={() => revokeWriter(confirmRevoke)} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WritersAdmin;
